import { Box, Button, IconButton } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	RecognizedLabelsStructureType,
	TableCellCandidateType,
	TableColumnDraft,
	TableRecognizedLabel,
} from 'entities/Files/types';
import { ColumnPreference } from 'entities/Preferences/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import AddTableForm from 'components/file-editor-components/add-table-form';
import DeleteTableModal from 'components/file-editor-components/delete-table-modal';
import Collapse from 'components/collapse';
import { CellTarget } from 'components/file-editor-components/table-fields/types';
import SingleTable from 'components/file-editor-components/table-fields/SingleTable';

type TableFieldsProps = {
	labels: RecognizedLabelsStructureType[];
	selectedSourceId?: string | null;
	onSelectSource?: (sourceId: string | null) => void;
	drawMode?: boolean;
	pendingCellTarget?: CellTarget | null;
	onCellTargetClick?: (target: CellTarget) => void;
	onCellContentChange?: (target: CellTarget, content: string) => void;
	onCellCandidateSelect?: (target: CellTarget, candidate: TableCellCandidateType) => void;
	onCellDelete?: (target: CellTarget) => void;
	onDeleteTable?: (tableIndex: number) => void;
	onAddTable?: (tableName: string, columns: TableColumnDraft[]) => void;
	onUpdateTable?: (tableIndex: number, tableName: string, columns: TableColumnDraft[]) => void;
	onAddRow?: (tableIndex: number) => void;
	onDeleteRow?: (tableIndex: number, rowIndex: number) => void;
	onDeleteColumn?: (tableIndex: number, columnIndex: number) => void;
	onReorderColumns?: (tableIndex: number, columnOrder: number[]) => void;
	getColumnPinPreferences?: (tableLabel: string | undefined) => ColumnPreference[];
	onTogglePinColumn?: (tableIndex: number, columnIndex: number) => void;
};

const TableFields: FC<TableFieldsProps> = ({
	labels,
	selectedSourceId,
	onSelectSource,
	drawMode,
	pendingCellTarget,
	onCellTargetClick,
	onCellContentChange,
	onCellCandidateSelect,
	onCellDelete,
	onDeleteTable,
	onAddTable,
	onUpdateTable,
	onAddRow,
	onDeleteRow,
	onDeleteColumn,
	onReorderColumns,
	getColumnPinPreferences,
	onTogglePinColumn,
}) => {
	const { t } = useTranslation();
	const [pendingDeleteTableIndex, setPendingDeleteTableIndex] = useState<number | null>(null);
	const [isAddTableFormOpen, setIsAddTableFormOpen] = useState(false);
	const [editingTableIndex, setEditingTableIndex] = useState<number | null>(null);

	const tables = useMemo(
		() =>
			labels
				.map((label, labelIndex) => ({ label, tableIndex: labelIndex }))
				.filter(
					(entry): entry is { label: TableRecognizedLabel; tableIndex: number } =>
						entry.label.type === 'table',
				),
		[labels],
	);

	const handleConfirmDelete = () => {
		if (pendingDeleteTableIndex !== null) onDeleteTable?.(pendingDeleteTableIndex);
		setPendingDeleteTableIndex(null);
		setEditingTableIndex(null);
	};

	const handleSaveTable = (tableName: string, columns: TableColumnDraft[]) => {
		onAddTable?.(tableName, columns);
		setIsAddTableFormOpen(false);
	};

	const handleUpdateTable = (tableIndex: number, tableName: string, columns: TableColumnDraft[]) => {
		onUpdateTable?.(tableIndex, tableName, columns);
		setEditingTableIndex(null);
	};

	return (
		<>
			{tables.map(({ label: table, tableIndex }) =>
				editingTableIndex === tableIndex ? (
					<AddTableForm
						key={tableIndex}
						initialTableName={table.label}
						initialColumns={table.columnsHeaders.map((header, columnIndex) => ({
							columnName: header.columnName,
							sourceIndex: columnIndex,
						}))}
						onCancel={() => setEditingTableIndex(null)}
						onSave={(tableName, columns) => handleUpdateTable(tableIndex, tableName, columns)}
					/>
				) : (
					<Collapse
						key={tableIndex}
						expandSignal={
							selectedSourceId?.startsWith(`cell-${tableIndex}-`) ? selectedSourceId : undefined
						}
						label={table?.label}
						headerExtra={
							(onUpdateTable || onDeleteTable) && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
									{onUpdateTable && (
										<IconButton
											onClick={() => setEditingTableIndex(tableIndex)}
											size={'small'}
											sx={{ padding: '4px' }}
										>
											<Icon
												id={'edit'}
												color={ColorPalette.royalBlue}
												style={{ width: '18px', height: '18px' }}
											/>
										</IconButton>
									)}
									{onDeleteTable && (
										<IconButton
											onClick={() => setPendingDeleteTableIndex(tableIndex)}
											size={'small'}
											sx={{ padding: '4px' }}
										>
											<Icon
												id={'close_round'}
												color={ColorPalette.violetRed}
												style={{ width: '18px', height: '18px' }}
											/>
										</IconButton>
									)}
								</Box>
							)
						}
					>
						<SingleTable
							table={table}
							tableIndex={tableIndex}
							selectedSourceId={selectedSourceId}
							onSelectSource={onSelectSource}
							drawMode={drawMode}
							pendingCellTarget={pendingCellTarget}
							onCellTargetClick={onCellTargetClick}
							onCellContentChange={onCellContentChange}
							onCellCandidateSelect={onCellCandidateSelect}
							onCellDelete={onCellDelete}
							onAddRow={onAddRow}
							onDeleteRow={onDeleteRow}
							onDeleteColumn={onDeleteColumn}
							onReorderColumns={onReorderColumns}
							getColumnPinPreferences={getColumnPinPreferences}
							onTogglePinColumn={onTogglePinColumn}
						/>
					</Collapse>
				),
			)}
			{onAddTable &&
				(isAddTableFormOpen ? (
					<AddTableForm onCancel={() => setIsAddTableFormOpen(false)} onSave={handleSaveTable} />
				) : (
					<Button
						size={'small'}
						variant={'outlined'}
						onClick={() => setIsAddTableFormOpen(true)}
						sx={{ alignSelf: 'flex-start' }}
					>
						{t('file_editor.add_table')}
					</Button>
				))}

			<DeleteTableModal
				open={pendingDeleteTableIndex !== null}
				onCancel={() => setPendingDeleteTableIndex(null)}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};

export default TableFields;
